import { FunctionComponent, memo, useMemo } from "react";
import classNames from "classnames";
import UrlTableMenuFilterList from "components-for/tables/url-table-menu-filter-list";
import {
    Cell,
    Column,
    EnumLabel,
    formatEnumOptions,
    Heading,
    TableMenuSection,
    useQueryParameters,
} from "@edgetier/client-components";
import { LIVE_AGENTS_TABLE_PREFIX } from "constants/pages/live";
import { AgentStateType, IAgent } from "@edgetier/watchtower-types";
import { IAgentStateLookup, IProps } from "./live-agents-table-state-column.types";
import { createStateClassName } from "./live-agents-table-state-column.utilities";
import "./live-agents-table-state-column.scss";

/**
 * Display agents' current state that can be filtered.
 * @param props.agentStates     All agent states from database
 * @returns Agent state column.
 */
const LiveAgentsTableStateColumn: FunctionComponent<IProps> = ({ agentStates }) => {
    const { agentStateType } = useQueryParameters(
        { agentStateType: String, orderBy: String },
        { prefix: LIVE_AGENTS_TABLE_PREFIX }
    );

    // Create a key-value object using agent state id and agent state type for faster lookup
    // when creating class names
    const agentStatesLookup = useMemo(() => {
        if (Array.isArray(agentStates) && agentStates.length > 0) {
            const lookupStates: IAgentStateLookup = agentStates.reduce(
                (result, agentState) => ({
                    ...result,
                    [agentState.agentStateId]: agentState.agentStateType,
                }),
                {}
            );
            return lookupStates;
        }
        return {};
    }, [agentStates]);

    return (
        <Column>
            <Heading
                isFiltered={typeof agentStateType === "string"}
                sortProperty="agentState"
                menu={
                    <>
                        <TableMenuSection>
                            <UrlTableMenuFilterList
                                labelProperty="label"
                                items={formatEnumOptions(AgentStateType, [AgentStateType.Offline])}
                                queryParameter="agentStateType"
                                queryParameterPrefix={LIVE_AGENTS_TABLE_PREFIX}
                                valueProperty="value"
                            />
                        </TableMenuSection>
                    </>
                }
            >
                State
            </Heading>
            <Cell<Pick<IAgent, "agentState" | "agentStateId">>>
                {({ agentState, agentStateId }) => (
                    <div
                        className={classNames(
                            "live-agents-table-state-column",
                            createStateClassName(agentStateId, agentStatesLookup)
                        )}
                    >
                        <EnumLabel>{agentState}</EnumLabel>
                    </div>
                )}
            </Cell>
        </Column>
    );
};

export default memo(LiveAgentsTableStateColumn);
